import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { MicroempresarioService } from '../../services/microempresario.service';
import { UserService } from '../../../services/user.service';
import {
  PerfilSolicitaCreditoDraft,
  parseMontoDigits,
  parsePlazoNumber,
  readSolicitaCreditoDraft,
  writeSolicitaCreditoDraft,
} from '../segmento/perfil-segmento.util';
import {
  PerfilDocumentoDef,
  UploadedTipoDocumento,
} from '../progress/perfil-progress.definitions';
import { PerfilProgressService } from '../progress/perfil-progress.service';
import { PerfilSeccionId } from '../../data/perfil-microempresario.content';
import { PerfilSegmentoService } from '../segmento/perfil-segmento.service';
import { ParametrosService } from '../../../services/parametros.service';
import { PerfilDocumentosService } from '../documentos/perfil-documentos.service';
import {
  intersectSeccionDocsConSolicitud,
  mergeSolicitudTiposEnSeccion,
} from '../documentos/perfil-documentos.util';
import {
  isPerfilJuridica,
  perfilSeccionOrder,
  resolveSaveSolicitudEndpoint,
  resolveTipoDocumentosPerfilApi,
} from '../perfil-tipo.util';
import {
  PerfilCargaSolicitud,
  PerfilDocUploadResult,
  PerfilEmpresarioCache,
  PerfilRedSocialDetalle,
  PerfilSaveResult,
  PerfilSolicitudCache,
} from './perfil-solicitud.models';
import {
  buildProgressValuesFromBackend,
  unwrapDocumentosSolicitudResponse,
} from '../progress/perfil-progress-hydrate';
import { countObligatoriosConHabilitadoCero } from '../documentos/perfil-archivo-slot.util';
import { UtmTrackingService } from '../../../services/utm-tracking.service';

/**
 * Orquesta creación / carga / guardado de solicitud perfil v2 (PN y PJ)
 * reutilizando MicroempresarioService (sin endpoints nuevos).
 */
@Injectable({ providedIn: 'root' })
export class PerfilSolicitudService {
  private readonly cargaSubject = new BehaviorSubject<PerfilCargaSolicitud | null>(
    null
  );
  private saving = false;
  /**
   * Tras "Solicitar crédito" / prepararNueva: no reutilizar la última solicitud
   * que devolvería cargarDatos(null) (p. ej. una ENVIADA).
   */
  private forceNuevaSolicitud = false;
  /**
   * Nombres (stem sin extensión, lower) ya cargados en la solicitud.
   * Replica la regla de files.form: no permitir el mismo archivo más de una vez.
   */
  private readonly nombresArchivosCargados = new Set<string>();
  private lastPersistedConteo: {
    camposLlenados: number;
    camposObligatorios: number;
    porcentajeSobreTi?: number | null;
    porcentajeSobreNegocio?: number | null;
    porcentajeSobreIngresos?: number | null;
  } | null = null;

  readonly carga$ = this.cargaSubject.asObservable();

  constructor(
    private readonly microempresario: MicroempresarioService,
    private readonly user: UserService,
    private readonly segmento: PerfilSegmentoService,
    private readonly parametros: ParametrosService,
    private readonly progress: PerfilProgressService,
    private readonly documentos: PerfilDocumentosService,
    private readonly utmTracking: UtmTrackingService
  ) {}

  get idSolicitud(): string | null {
    const fromCache = this.cargaSubject.value?.solicitud?.idSolicitud;
    if (fromCache) {
      return String(fromCache);
    }
    const fromUser = this.user.getUser()?.idSolicitud;
    if (fromUser) {
      return String(fromUser);
    }
    const draft = readSolicitaCreditoDraft() as PerfilSolicitaCreditoDraft & {
      idSolicitud?: string;
    };
    return draft?.idSolicitud ? String(draft.idSolicitud) : null;
  }

  get empresarioSnapshot(): PerfilEmpresarioCache | null {
    return this.cargaSubject.value?.empresario ?? null;
  }

  get solicitudSnapshot(): PerfilSolicitudCache | null {
    return this.cargaSubject.value?.solicitud ?? null;
  }

  cargarObservacionesMicroempresario(): Observable<string | null> {
    const idSolicitud = this.idSolicitud;
    if (!idSolicitud) {
      return of(null);
    }
    return this.microempresario.getObservacionesMicro(idSolicitud).pipe(
      map((response: any) =>
        response?.exitoso ? String(response.observaciones ?? '') : null
      ),
      catchError(() => of(null))
    );
  }

  guardarObservacionesMicroempresario(
    observaciones: string
  ): Observable<PerfilSaveResult> {
    const idSolicitud = this.idSolicitud;
    if (!idSolicitud) {
      return of({
        exitoso: false,
        error: 'No se encontró la solicitud para guardar las observaciones',
      });
    }
    return this.microempresario
      .guardarObservacionesMicro({
        idSolicitud,
        observacionesMicroempresario: observaciones,
      })
      .pipe(
        map((response: any) => ({
          exitoso: !!response?.exitoso,
          error: response?.error,
        })),
        catchError(() =>
          of({
            exitoso: false,
            error: 'No se pudieron guardar las observaciones',
          })
        )
      );
  }

  get redesDetalleSnapshot(): PerfilRedSocialDetalle[] {
    const detalle = this.cargaSubject.value?.redesSocialesDetalle;
    return Array.isArray(detalle) ? detalle : [];
  }

  /** Retoma un borrador / devuelta / en curso ya existente. */
  prepararRetomar(
    idSolicitud: string,
    extras: Partial<PerfilSolicitaCreditoDraft> = {}
  ): void {
    this.forceNuevaSolicitud = false;
    // No mezclar draft/%/docs de otra solicitud (Express previa, etc.).
    this.limpiarContextoCliente();
    this.persistIdSolicitud(String(idSolicitud));
    writeSolicitaCreditoDraft({
      idSolicitud: String(idSolicitud),
      ...extras,
    });
  }

  /** Limpia id de solicitud para iniciar un flujo nuevo. */
  prepararNueva(): void {
    this.forceNuevaSolicitud = true;
    const user = this.user.getUser();
    if (user) {
      delete user.idSolicitud;
      this.user.saveUser(user);
    }
    this.limpiarContextoCliente();
  }

  /**
   * Borra draft de sessionStorage + progreso en memoria + catálogo docs.
   * Llamar al cambiar de solicitud (nueva, retomar otra, enviar, ver detalle).
   */
  limpiarContextoCliente(): void {
    this.cargaSubject.next(null);
    this.lastPersistedConteo = null;
    this.nombresArchivosCargados.clear();
    writeSolicitaCreditoDraft({});
    this.resetProgresoEnMemoria();
  }

  /**
   * Docs son por solicitud (no se reutilizan). Campos empresario sí pueden
   * precargarse después en cada sección. Al crear, el hub debe partir en 0 %.
   */
  private resetProgresoEnMemoria(): void {
    this.progress.replaceAllSectionValues({
      'sobre-ti': {},
      'sobre-negocio': {},
      'sobre-ingresos': {},
    });
    this.documentos.invalidateCatalogCache();
    this.lastPersistedConteo = null;
  }

  /**
   * Primera pantalla: crea (o actualiza borrador) con monto/plazo/destino
   * y conserva el resto de datos ya conocidos del empresario.
   */
  crearDesdeSolicitaCredito(
    draftExtra: Partial<PerfilSolicitaCreditoDraft> & {
      destinosLabels?: string[];
    } = {}
  ): Observable<PerfilSaveResult> {
    if (this.saving) {
      return of({ exitoso: false, error: 'Guardado en curso' });
    }
    this.saving = true;
    const draft = { ...readSolicitaCreditoDraft(), ...draftExtra };
    const monto = parseMontoDigits(draft.monto);
    const plazo = parsePlazoNumber(draft.plazo);
    const otroTexto =
      (typeof draftExtra.otroTexto === 'string' && draftExtra.otroTexto.trim()) ||
      (typeof draft.otroTexto === 'string' && draft.otroTexto.trim()) ||
      '';
    // DESTINO (columna): solo texto libre de "Otro". Las opciones van en N:M (idsDestinoCredito).
    const destino = otroTexto;
    const idsDestinoCredito = normalizeIdsDestinoCredito(
      draftExtra.idsDestinoCredito ?? draft.idsDestinoCredito
    );
    const tipoLinea = draftExtra.tipoLinea ?? draft.tipoLinea ?? null;
    // Si no hay id en sesión/draft (o viene de prepararNueva), no reutilizar la última del back.
    const crearNueva = this.forceNuevaSolicitud || !this.idSolicitud;
    if (crearNueva) {
      this.resetProgresoEnMemoria();
    }

    return this.cargarDatos(null, { adoptSolicitud: !crearNueva }).pipe(
      switchMap((carga) => {
        if (!carga.exitoso || !carga.empresario) {
          return of({
            exitoso: false,
            error: carga.error || 'No se pudo cargar el empresario',
          } as PerfilSaveResult);
        }
        // Nueva solicitud: no persistir % stale (Express previa / docs de otra).
        // Docs se cargan vacíos por idSolicitud; campos se hidratan al abrir sección.
        const progreso = crearNueva
          ? {
              camposLlenados: 0,
              camposObligatorios: 0,
              porcentajeSobreTi: 0,
              porcentajeSobreNegocio: 0,
              porcentajeSobreIngresos: 0,
            }
          : this.resolveProgresoPatch({}, carga.solicitud);
        const body = this.buildBodyFromEmpresario(carga.empresario, {
          // Nueva: siempre null → INSERT borrador. Retoma: id existente.
          idSolicitud: crearNueva
            ? null
            : this.idSolicitud || carga.solicitud?.idSolicitud || null,
          monto,
          plazo,
          destino,
          tipoLinea,
          idsDestinoCredito,
          redesSociales: carga.redesSociales,
          redesSocialesDetalle: carga.redesSocialesDetalle,
          camposLlenados: progreso.camposLlenados,
          camposObligatorios: progreso.camposObligatorios,
          porcentajeSobreTi: progreso.porcentajeSobreTi,
          porcentajeSobreNegocio: progreso.porcentajeSobreNegocio,
          porcentajeSobreIngresos: progreso.porcentajeSobreIngresos,
        });
        return this.microempresario
          .saveSolicitud(this.saveEndpoint(), body)
          .pipe(
          map((raw: unknown) => {
            const data = raw as PerfilSaveResult;
            if (data?.exitoso && data.idSolicitudCredito) {
              this.forceNuevaSolicitud = false;
              this.persistIdSolicitud(String(data.idSolicitudCredito));
              this.utmTracking.registrarSolicitudCreada(String(data.idSolicitudCredito));
              const prev = this.cargaSubject.value;
              if (prev) {
                this.cargaSubject.next({
                  ...prev,
                  solicitud: {
                    ...(prev.solicitud ?? {}),
                    idSolicitud: String(data.idSolicitudCredito),
                    idsDestinoCredito,
                  },
                });
              }
            }
            if (data?.exitoso) {
              this.lastPersistedConteo = progreso;
            }
            return {
              exitoso: !!data?.exitoso,
              idSolicitudCredito: data?.idSolicitudCredito
                ? String(data.idSolicitudCredito)
                : undefined,
              error: data?.error,
            };
          }),
          catchError(() =>
            of({
              exitoso: false,
              error: 'Error al guardar la solicitud',
            } as PerfilSaveResult)
          )
        );
      }),
      tap(() => {
        this.saving = false;
      }),
      catchError(() => {
        this.saving = false;
        return of({
          exitoso: false,
          error: 'Error al crear la solicitud',
        } as PerfilSaveResult);
      })
    );
  }

  /** Carga empresario + solicitud (si existe) y cachea. */
  ensureCargado(force = false): Observable<PerfilCargaSolicitud> {
    if (!force && this.cargaSubject.value?.exitoso) {
      return of(this.cargaSubject.value);
    }
    // Si aún no hay id y estamos creando nueva, no adoptar la última del back.
    if (this.forceNuevaSolicitud && !this.idSolicitud) {
      return this.cargarDatos(null, { adoptSolicitud: false });
    }
    return this.cargarDatos(this.idSolicitud);
  }

  cargarDatos(
    idSolicitud: string | null,
    opts: { adoptSolicitud?: boolean } = {}
  ): Observable<PerfilCargaSolicitud> {
    const adoptSolicitud = opts.adoptSolicitud !== false;
    const user = this.user.getUser();
    const idEmpresario = Number(user?.idEmpresario);
    if (!idEmpresario) {
      const empty: PerfilCargaSolicitud = {
        empresario: null,
        solicitud: null,
        redesSociales: [],
        redesSocialesDetalle: [],
        exitoso: false,
        error: 'Sin empresario en sesión',
      };
      this.cargaSubject.next(empty);
      return of(empty);
    }
    return this.microempresario
      .cargarDatos({
        idEmpresario,
        idSolicitud: idSolicitud || null,
      })
      .pipe(
        map((raw: unknown) => {
          const data = raw as {
            exitoso?: boolean;
            Empresario?: PerfilEmpresarioCache;
            solicitud?: PerfilSolicitudCache | null;
            RedesSociales?: string[];
            idsDestinoCredito?: unknown;
            error?: string;
          };
          // Con adoptSolicitud=false (flujo nueva) solo usamos el empresario;
          // el back puede devolver la última ENVIADA y no debe reutilizarse.
          const solicitudBase = adoptSolicitud ? data?.solicitud ?? null : null;
          const idsDestinoCredito = normalizeIdsDestinoCredito(
            data?.idsDestinoCredito ??
              (solicitudBase as { idsDestinoCredito?: unknown } | null)
                ?.idsDestinoCredito
          );
          const solicitud = solicitudBase
            ? { ...solicitudBase, idsDestinoCredito }
            : null;
          if (adoptSolicitud && solicitud?.idSolicitud) {
            this.persistIdSolicitud(String(solicitud.idSolicitud));
          }
          this.lastPersistedConteo = solicitud
            ? {
                camposLlenados: Number(solicitud.camposLlenados ?? 0),
                camposObligatorios: Number(solicitud.camposObligatorios ?? 0),
                porcentajeSobreTi: solicitud.porcentajeSobreTi ?? null,
                porcentajeSobreNegocio: solicitud.porcentajeSobreNegocio ?? null,
                porcentajeSobreIngresos:
                  solicitud.porcentajeSobreIngresos ?? null,
              }
            : null;
          const redesRaw = data?.RedesSociales;
          const redesDetalle = adoptSolicitud
            ? normalizeRedesDetalle(redesRaw)
            : [];
          const result: PerfilCargaSolicitud = {
            exitoso: data?.exitoso !== false,
            empresario: data?.Empresario ?? null,
            solicitud,
            redesSociales: redesDetalle.map((r) => r.url).filter(Boolean),
            redesSocialesDetalle: redesDetalle,
            error: data?.error,
          };
          this.cargaSubject.next(result);
          return result;
        }),
        catchError(() => {
          const fail: PerfilCargaSolicitud = {
            exitoso: false,
            empresario: null,
            solicitud: null,
            redesSociales: [],
            redesSocialesDetalle: [],
            error: 'Error al cargar datos de la solicitud',
          };
          this.cargaSubject.next(fail);
          return of(fail);
        })
      );
  }

  /**
   * Guarda borrador con patch parcial (sección) sobre el cache de empresario.
   */
  guardarBorrador(
    patch: Partial<PerfilEmpresarioCache> & {
      monto?: number;
      plazo?: number;
      destino?: string;
      tipoLinea?: string | null;
      idsDestinoCredito?: number[];
      redesSociales?: string[];
      redesSocialesDetalle?: PerfilRedSocialDetalle[];
      camposLlenados?: number;
      camposObligatorios?: number;
      porcentajeSobreTi?: number | null;
      porcentajeSobreNegocio?: number | null;
      porcentajeSobreIngresos?: number | null;
      /** Check autorización centrales. */
      autorizacionCentrales?: boolean | null;
    } = {}
  ): Observable<PerfilSaveResult> {
    return this.ensureCargado().pipe(
      switchMap((carga) => {
        if (!carga.exitoso || !carga.empresario) {
          return of({
            exitoso: false,
            error: carga.error || 'Sin datos de empresario',
          } as PerfilSaveResult);
        }
        const draft = readSolicitaCreditoDraft();
        const monto =
          patch.monto ??
          Number(carga.solicitud?.monto) ??
          parseMontoDigits(draft?.monto);
        const plazo =
          patch.plazo ??
          Number(carga.solicitud?.plazo) ??
          parsePlazoNumber(draft?.plazo);
        const destino =
          patch.destino ??
          (typeof draft?.otroTexto === 'string' && draft.otroTexto.trim()
            ? draft.otroTexto.trim()
            : null) ??
          String(carga.solicitud?.destino ?? '') ??
          '';
        const tipoLinea =
          patch.tipoLinea ??
          draft?.tipoLinea ??
          (carga.solicitud?.tipoLinea as string | null | undefined) ??
          null;
        const idsDestinoCredito = normalizeIdsDestinoCredito(
          patch.idsDestinoCredito ??
            draft?.idsDestinoCredito ??
            carga.solicitud?.idsDestinoCredito
        );

        const merged: PerfilEmpresarioCache = {
          ...carga.empresario,
          ...patch,
        };
        const progreso = this.resolveProgresoPatch(patch, carga.solicitud);
        /** Siempre boolean para no dejar el stamp de saveSolicitudPendiente. */
        const autorizacionCentrales =
          patch.autorizacionCentrales !== undefined
            ? !!patch.autorizacionCentrales
            : !!carga.solicitud?.apectacionCentrales;
        const redesSocialesDetalle =
          patch.redesSocialesDetalle ?? carga.redesSocialesDetalle;
        /** Con detalle, el back ignora el array de URLs sueltas; no lo duplicamos en el payload. */
        const redesSociales =
          redesSocialesDetalle != null
            ? []
            : patch.redesSociales ?? carga.redesSociales;

        this.cargaSubject.next({
          ...carga,
          empresario: merged,
          redesSociales,
          redesSocialesDetalle,
          solicitud: {
            ...(carga.solicitud ?? {}),
            ...progreso,
            idsDestinoCredito,
            apectacionCentrales: autorizacionCentrales
              ? carga.solicitud?.apectacionCentrales ?? new Date().toISOString()
              : null,
          },
        });

        const body = this.buildBodyFromEmpresario(merged, {
          idSolicitud: this.idSolicitud || carga.solicitud?.idSolicitud || null,
          monto,
          plazo,
          destino,
          tipoLinea,
          idsDestinoCredito,
          redesSociales,
          redesSocialesDetalle,
          camposLlenados: progreso.camposLlenados,
          camposObligatorios: progreso.camposObligatorios,
          porcentajeSobreTi: progreso.porcentajeSobreTi,
          porcentajeSobreNegocio: progreso.porcentajeSobreNegocio,
          porcentajeSobreIngresos: progreso.porcentajeSobreIngresos,
          autorizacionCentrales,
        });

        return this.microempresario
          .saveSolicitud(this.saveEndpoint(), body)
          .pipe(
          map((raw: unknown) => {
            const data = raw as PerfilSaveResult;
            if (data?.exitoso && data.idSolicitudCredito) {
              this.persistIdSolicitud(String(data.idSolicitudCredito));
            }
            if (data?.exitoso) {
              this.lastPersistedConteo = progreso;
            }
            return {
              exitoso: !!data?.exitoso,
              idSolicitudCredito: data?.idSolicitudCredito
                ? String(data.idSolicitudCredito)
                : undefined,
              error: data?.error,
            };
          }),
          catchError(() =>
            of({
              exitoso: false,
              error: 'Error al guardar la solicitud',
            } as PerfilSaveResult)
          )
        );
      })
    );
  }

  /** Persiste conteo global + % de secciones con valores en memoria. */
  persistirCamposProgreso(): Observable<PerfilSaveResult | null> {
    if (!this.idSolicitud) {
      return of(null);
    }
    // Antes de contar: hidratar las 3 secciones desde empresario+docs
    // (si no, al guardar Sobre ti se pisa 29/30 → 7/30).
    return this.hidratarProgresoGlobal().pipe(
      switchMap(() => {
        const progreso = this.resolveProgresoPatch(
          {},
          this.cargaSubject.value?.solicitud
        );
        if (
          this.lastPersistedConteo &&
          this.lastPersistedConteo.camposLlenados === progreso.camposLlenados &&
          this.lastPersistedConteo.camposObligatorios ===
            progreso.camposObligatorios &&
          this.lastPersistedConteo.porcentajeSobreTi ===
            progreso.porcentajeSobreTi &&
          this.lastPersistedConteo.porcentajeSobreNegocio ===
            progreso.porcentajeSobreNegocio &&
          this.lastPersistedConteo.porcentajeSobreIngresos ===
            progreso.porcentajeSobreIngresos
        ) {
          return of(null);
        }
        return this.guardarBorrador(progreso).pipe(
          tap((res) => {
            if (res?.exitoso) {
              this.lastPersistedConteo = progreso;
            }
          })
        );
      })
    );
  }

  /**
   * Reconstruye avance de las 3 secciones desde back + docs cargados.
   * Los valores ya vivos en memoria (sección abierta) ganan por clave.
   */
  hidratarProgresoGlobal(): Observable<void> {
    if (!this.idSolicitud) {
      return of(undefined);
    }
    return forkJoin({
      carga: this.ensureCargado(),
      docs: this.documentos.ensureLoaded(),
      uploaded: this.cargarDocumentosSolicitud(),
    }).pipe(
      tap(({ carga, docs, uploaded }) => {
        if (!carga.exitoso || !carga.empresario) {
          return;
        }
        const vigentes = this.docsVigentesParaProgreso(docs, uploaded);
        this.progress.setDocumentosConfig(vigentes);
        const fromBack = buildProgressValuesFromBackend(
          carga,
          vigentes,
          uploaded
        );
        const mergeSeccion = (id: PerfilSeccionId) => {
          const live = this.progress.getSectionValues(id);
          const base = fromBack[id] ?? {};
          if (!Object.keys(live).length) {
            return { ...base };
          }
          return { ...base, ...live };
        };
        this.progress.replaceAllSectionValues({
          'sobre-ti': mergeSeccion('sobre-ti'),
          'sobre-negocio': mergeSeccion('sobre-negocio'),
          'sobre-ingresos': mergeSeccion('sobre-ingresos'),
        });
      }),
      map(() => undefined),
      catchError(() => of(undefined))
    );
  }

  
  private docsVigentesParaProgreso(
    catalog: ReadonlyArray<PerfilDocumentoDef>,
    uploaded: {
      archivos: UploadedTipoDocumento[];
      fotos: UploadedTipoDocumento[];
    }
  ): PerfilDocumentoDef[] {
    const tieneTipos =
      (uploaded.archivos?.length ?? 0) + (uploaded.fotos?.length ?? 0) > 0;
    if (!tieneTipos) {
      return [...catalog];
    }
    const juridica = isPerfilJuridica(this.user.getUser()?.tipoPersona);
    const result: PerfilDocumentoDef[] = [];
    for (const seccionId of perfilSeccionOrder(
      this.user.getUser()?.tipoPersona
    )) {
      let filtered = intersectSeccionDocsConSolicitud(
        catalog,
        uploaded,
        seccionId,
        { juridica }
      );
      if (juridica && seccionId === 'sobre-ti') {
        filtered = mergeSolicitudTiposEnSeccion(
          filtered,
          uploaded,
          seccionId,
          { juridica: true }
        );
      }
      result.push(...filtered);
    }
    return result;
  }

  /** Envía formalmente la solicitud (estado borrador → creada). */
  enviarSolicitud(): Observable<PerfilSaveResult> {
    const id = this.idSolicitud;
    if (!id) {
      return of({
        exitoso: false,
        error: 'No hay solicitud para enviar. Completa monto y plazo primero.',
      });
    }
    return this.microempresario.crearSolicitud(id).pipe(
      map((raw: unknown) => {
        const data = raw as { exitoso?: boolean; error?: string };
        const ok = !!data?.exitoso;
        if (ok) {
          // No dejar draft/% de la enviada para la próxima solicitud.
          this.utmTracking.registrarSolicitudEnviada(String(id));
          this.limpiarContextoTrasEnvio();
        }
        return {
          exitoso: ok,
          idSolicitudCredito: id,
          error: data?.error,
        };
      }),
      catchError(() =>
        of({
          exitoso: false,
          error: 'Error al enviar la solicitud',
        } as PerfilSaveResult)
      )
    );
  }

  validarRegistroParaEnvio(): Observable<{ ok: boolean }> {
    const user = this.user.getUser();
    const idEmpresario = Number(user?.idEmpresario);
    if (!idEmpresario) {
      return of({ ok: false });
    }
    const req$ = isPerfilJuridica(user?.tipoPersona)
      ? this.microempresario.getValidarRegistroJuridico(idEmpresario)
      : this.microempresario.getValidarRegistroNatural(
          user?.idEmpresarioNatural,
          idEmpresario
        );
    return req$.pipe(
      map((raw: unknown) => {
        const data = raw as { exitoso?: boolean };
        return { ok: !!data?.exitoso };
      }),
      catchError(() => of({ ok: false }))
    );
  }

  private limpiarContextoTrasEnvio(): void {
    this.forceNuevaSolicitud = false;
    const user = this.user.getUser();
    if (user) {
      delete user.idSolicitud;
      this.user.saveUser(user);
    }
    this.limpiarContextoCliente();
  }

  /** Sube archivo (documento o foto) asociado a la solicitud. */
  subirDocumento(
    file: File,
    doc: PerfilDocumentoDef,
    opts: { esFoto?: boolean; excludeNombreArchivo?: string } = {}
  ): Observable<PerfilDocUploadResult> {
    const idSolicitud = this.idSolicitud;
    if (!idSolicitud) {
      return of({
        exitoso: false,
        error: 'No hay solicitud creada. Guarda monto y plazo primero.',
      });
    }
    const idTipoDocumento = Number(doc.idTipoDocumento ?? doc.id);
    if (!idTipoDocumento) {
      return of({ exitoso: false, error: 'Tipo de documento inválido' });
    }

    const claveNueva = this.claveNombreArchivo(file.name);
    const claveExcluida = this.claveNombreArchivo(opts.excludeNombreArchivo ?? '');
    if (
      claveNueva &&
      this.nombresArchivosCargados.has(claveNueva) &&
      claveNueva !== claveExcluida
    ) {
      return of({
        exitoso: false,
        error: 'No puedes subir el mismo archivo.',
      });
    }

    return new Observable<PerfilDocUploadResult>((subscriber) => {
      const reader = new FileReader();
      reader.onerror = () => {
        subscriber.next({ exitoso: false, error: 'No se pudo leer el archivo' });
        subscriber.complete();
      };
      reader.onload = () => {
        const base64 = String(reader.result ?? '').split(',')[1] ?? '';
        const parts = file.name.split('.');
        const extension = parts[parts.length - 1] || '';
        const esFoto = opts.esFoto ?? !!doc.esFoto;
        const esVideo = [4, 12, 41].includes(idTipoDocumento);
        const esMultimediaAws = esFoto || esVideo;
        const json: Record<string, unknown> = {
          idSolicitud,
          idTipoDocumento,
          tipoContenido: file.type,
          extension,
          archivo: base64,
        };
        if (!esMultimediaAws) {
          json.nombreDocumento = doc.nombreArchivoGuardado || doc.codigo || doc.label;
        }
        const req$ = esMultimediaAws
          ? this.microempresario.saveFileAWS(json)
          : this.microempresario.saveFile(json);
        req$.subscribe({
          next: (raw: unknown) => {
            const data = raw as {
              exitoso?: boolean;
              idDocumentoSolicitud?: string | number;
              error?: string;
              estadoDocumentoOCR?: number | null;
              observacionDocumentoOCR?: string | null;
              habilitado?: number | null;
              pesoArchivo?: number | null;
            };
            const ocrEstado =
              data?.estadoDocumentoOCR != null
                ? Number(data.estadoDocumentoOCR)
                : null;
            const ocrFallido = ocrEstado === 0;
            if (data?.exitoso) {
              this.registrarNombreTrasSubida(claveNueva, claveExcluida);
            }
            subscriber.next({
              exitoso: !!data?.exitoso,
              idDocumentoSolicitud: data?.idDocumentoSolicitud,
              error: ocrFallido
                ? String(data?.observacionDocumentoOCR || '').trim() ||
                  'Los documentos cargados presentan inconsistencias'
                : data?.error,
              estadoDocumentoOCR: ocrEstado,
              observacionDocumentoOCR: data?.observacionDocumentoOCR ?? null,
              habilitado:
                data?.habilitado != null ? Number(data.habilitado) : null,
              pesoArchivo:
                data?.pesoArchivo != null ? Number(data.pesoArchivo) : null,
            });
            subscriber.complete();
          },
          error: () => {
            subscriber.next({
              exitoso: false,
              error: 'Error al subir el archivo',
            });
            subscriber.complete();
          },
        });
      };
      reader.readAsDataURL(file);
    });
  }

  eliminarDocumento(
    idDocumento: string | number,
    opts: {
      esFoto?: boolean;
      idTipoDocumento?: number | string | null;
      nombreArchivo?: string | null;
    } = {}
  ): Observable<PerfilDocUploadResult> {
    const idSolicitud = this.idSolicitud;
    if (opts.esFoto) {
      // Mismo contrato que files.form: /eliminar-archivo-aws usa idTipoDocumento.
      const idTipoDocumento = Number(opts.idTipoDocumento);
      if (!idSolicitud || !idTipoDocumento) {
        return of({
          exitoso: false,
          error: 'No se pudo identificar el archivo a eliminar',
        });
      }
      return this.microempresario
        .deleteFileAWS({
          idSolicitud,
          idTipoDocumento,
        })
        .pipe(
          map((raw: unknown) => {
            const data = raw as { exitoso?: boolean; error?: string };
            if (data?.exitoso) {
              this.liberarNombreArchivo(opts.nombreArchivo);
            }
            return {
              exitoso: !!data?.exitoso,
              error: data?.error,
            };
          }),
          catchError(() =>
            of({ exitoso: false, error: 'Error al eliminar el archivo' })
          )
        );
    }
    return this.microempresario.deleteFile(String(idDocumento)).pipe(
      map((raw: unknown) => {
        const data = raw as { exitoso?: boolean; error?: string };
        if (data?.exitoso) {
          this.liberarNombreArchivo(opts.nombreArchivo);
        }
        return {
          exitoso: !!data?.exitoso,
          error: data?.error,
        };
      }),
      catchError(() =>
        of({ exitoso: false, error: 'Error al eliminar el documento' })
      )
    );
  }

  /**
   * Documentos ya asociados a la solicitud (para precargar slots y obligatoriedad).
   * Usa el mismo endpoint que files.form.
   */
  cargarDocumentosSolicitud(): Observable<{
    archivos: UploadedTipoDocumento[];
    fotos: UploadedTipoDocumento[];
  }> {
    const empty = {
      archivos: [] as UploadedTipoDocumento[],
      fotos: [] as UploadedTipoDocumento[],
    };
    const user = this.user.getUser();
    const idEmpresario = Number(user?.idEmpresario);
    return this.segmento.ensureParamsLoaded().pipe(
      tap(() => this.segmento.refreshFromDraft()),
      switchMap(() => {
        const idSolicitud = this.idSolicitud;
        if (!idSolicitud || !idEmpresario) {
          return of(empty);
        }
        const end = resolveTipoDocumentosPerfilApi(
          user?.tipoPersona,
          this.segmento.segmento
        );
        return this.parametros
          .getDocumentosForUpload(end, idSolicitud, idEmpresario)
          .pipe(
            map((raw) => unwrapDocumentosSolicitudResponse(raw)),
            tap(({ archivos, fotos }) =>
              this.sincronizarNombresDesdeSolicitud(archivos, fotos)
            ),
            catchError(() => of(empty))
          );
      })
    );
  }

  /**
   * Gate de envío: bloquea si hay documentos obligatorios inválidos
   * (habilitado == 0 u OCR fallido).
   */
  validarDocumentosParaEnvio(): Observable<{
    ok: boolean;
    invalidCount: number;
  }> {
    return this.cargarDocumentosSolicitud().pipe(
      map(({ archivos, fotos }) => {
        const invalidCount = countObligatoriosConHabilitadoCero([
          ...archivos,
          ...fotos,
        ]);
        return { ok: invalidCount === 0, invalidCount };
      })
    );
  }

  get idEstadoSolicitud(): number | null {
    const raw = this.solicitudSnapshot?.idEstadoSolicitud_NeoEstadoSolicitud;
    if (raw == null) {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  private persistIdSolicitud(id: string): void {
    this.user.setIdSolicitud(id as unknown as number);
    const draft = readSolicitaCreditoDraft() ?? {};
    writeSolicitaCreditoDraft({
      ...draft,
      idSolicitud: id,
    });
    const current = this.cargaSubject.value;
    if (current) {
      this.cargaSubject.next({
        ...current,
        solicitud: {
          ...(current.solicitud ?? {}),
          idSolicitud: id,
        },
      });
    }
  }

  private resolveProgresoPatch(
    patch: {
      camposLlenados?: number | null;
      camposObligatorios?: number | null;
      porcentajeSobreTi?: number | null;
      porcentajeSobreNegocio?: number | null;
      porcentajeSobreIngresos?: number | null;
    },
    stored?: PerfilSolicitudCache | null
  ): {
    camposLlenados: number;
    camposObligatorios: number;
    porcentajeSobreTi: number | null;
    porcentajeSobreNegocio: number | null;
    porcentajeSobreIngresos: number | null;
  } {
    const conteo = this.progress.getCamposConteo();
    const livePercent = (id: PerfilSeccionId): number | null => {
      if (!Object.keys(this.progress.getSectionValues(id)).length) {
        return null;
      }
      return this.progress.getSectionProgress(id).percent;
    };
    return {
      // Conteos reales de campos/docs obligatorios (no el promedio*3 de %).
      camposLlenados: patch.camposLlenados ?? conteo.camposLlenados,
      camposObligatorios:
        patch.camposObligatorios ?? conteo.camposObligatorios,
      porcentajeSobreTi:
        patch.porcentajeSobreTi ??
        livePercent('sobre-ti') ??
        stored?.porcentajeSobreTi ??
        null,
      porcentajeSobreNegocio:
        patch.porcentajeSobreNegocio ??
        livePercent('sobre-negocio') ??
        stored?.porcentajeSobreNegocio ??
        null,
      porcentajeSobreIngresos:
        patch.porcentajeSobreIngresos ??
        livePercent('sobre-ingresos') ??
        stored?.porcentajeSobreIngresos ??
        null,
    };
  }

  private saveEndpoint(): 'natural' | 'juridico' {
    return resolveSaveSolicitudEndpoint(this.user.getUser()?.tipoPersona);
  }

  private buildBodyFromEmpresario(
    emp: PerfilEmpresarioCache,
    solicitud: {
      idSolicitud?: string | null;
      monto: number;
      plazo: number;
      destino: string;
      tipoLinea?: string | null;
      idsDestinoCredito?: number[];
      redesSociales?: string[] | null;
      redesSocialesDetalle?: PerfilRedSocialDetalle[] | null;
      camposLlenados?: number | null;
      camposObligatorios?: number | null;
      porcentajeSobreTi?: number | null;
      porcentajeSobreNegocio?: number | null;
      porcentajeSobreIngresos?: number | null;
      autorizacionCentrales?: boolean | null;
    }
  ): Record<string, unknown> {
    if (isPerfilJuridica(this.user.getUser()?.tipoPersona)) {
      return this.buildBodyJuridico(emp, solicitud);
    }
    return this.buildBodyNatural(emp, solicitud);
  }

  private buildBodyJuridico(
    emp: PerfilEmpresarioCache,
    solicitud: {
      idSolicitud?: string | null;
      monto: number;
      plazo: number;
      destino: string;
      tipoLinea?: string | null;
      idsDestinoCredito?: number[];
      redesSociales?: string[] | null;
      redesSocialesDetalle?: PerfilRedSocialDetalle[] | null;
      camposLlenados?: number | null;
      camposObligatorios?: number | null;
      porcentajeSobreTi?: number | null;
      porcentajeSobreNegocio?: number | null;
      porcentajeSobreIngresos?: number | null;
      autorizacionCentrales?: boolean | null;
    }
  ): Record<string, unknown> {
    const user = this.user.getUser();
    const toDate = (v: unknown): string | null => {
      if (v == null || v === '') {
        return null;
      }
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) {
        return v.slice(0, 10);
      }
      try {
        return new Date(v as string).toISOString().slice(0, 10);
      } catch {
        return null;
      }
    };
    const numOrNull = (v: unknown): number | null => {
      if (v == null || v === '') {
        return null;
      }
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const conteo = this.progress.getCamposConteo();
    const camposLlenados = solicitud.camposLlenados ?? conteo.camposLlenados;
    const camposObligatorios =
      solicitud.camposObligatorios ?? conteo.camposObligatorios;
    const barrioEmpresa =
      (emp.barrioEmpresa as string | undefined) ??
      emp.barrioResidencia ??
      '';
    const direccionEmpresa =
      (emp.direccionEmpresa as string | undefined) ??
      emp.direccionResidencia ??
      '';

    const codigoCiiu =
      emp.codigoCiiu != null && emp.codigoCiiu !== ''
        ? String(emp.codigoCiiu)
        : null;
    const ciiuNombre =
      emp.ciiuNombre != null && emp.ciiuNombre !== ''
        ? String(emp.ciiuNombre)
        : null;
    const minoriaNum = numOrNull(emp.minoria);
    const camara =
      emp.camaraComercioVigente == null
        ? null
        : Number(emp.camaraComercioVigente) === 1 ||
            emp.camaraComercioVigente === true
          ? 1
          : 0;

    return {
      idEmpresario: Number(emp.idEmpresario ?? user?.idEmpresario),
      razonSocial: emp.razonSocial ?? '',
      correoElectronico: emp.correoElectronico ?? '',
      idGenero: emp.idGenero != null && emp.idGenero !== '' ? emp.idGenero : null,
      minoria: minoriaNum,
      minoriaDescripcion:
        minoriaNum === 1 ? emp.minoriaDescripcion ?? null : null,
      camaraComercioVigente: camara,
      codigoCiiu,
      ciiuNombre,
      idDepartamento: numOrNull(emp.idDepartamento),
      idCiudad: numOrNull(emp.idCiudad),
      idLocalidad: numOrNull(emp.idLocalidad),
      barrioEmpresa,
      direccionEmpresa,
      creditoAnterior: numOrNull(emp.creditoAnterior),
      fechaCreditoAnterior:
        Number(emp.creditoAnterior) === 1
          ? toDate(emp.fechaCreditoAnterior)
          : null,
      idTipoSociedad: numOrNull(emp.idTipoSociedad),
      fechaRegistroActividadEconomica: toDate(
        emp.fechaRegistroActividadEconomica
      ),
      anioActivos: emp.anioActivos ?? '',
      nivelActivos: numOrNull(emp.nivelActivos) ?? 0,
      valorVentas: numOrNull(emp.valorVentas),
      autorizacionCentrales: solicitud.autorizacionCentrales ?? false,
      idPaisOrigen: numOrNull(emp.idPaisOrigen),
      redesSocialesDetalle: solicitud.redesSocialesDetalle ?? null,
      redesSociales:
        solicitud.redesSocialesDetalle != null
          ? null
          : solicitud.redesSociales ?? [],
      idsDestinoCredito: solicitud.idsDestinoCredito ?? [],
      camposLlenados,
      camposObligatorios,
      porcentajeSobreTi: solicitud.porcentajeSobreTi ?? null,
      porcentajeSobreNegocio: solicitud.porcentajeSobreNegocio ?? null,
      porcentajeSobreIngresos: solicitud.porcentajeSobreIngresos ?? null,
      solicitud: {
        idSolicitud: solicitud.idSolicitud || null,
        monto: solicitud.monto,
        plazo: solicitud.plazo,
        destino: solicitud.destino,
        tipoLinea: solicitud.tipoLinea || null,
        idUsuario_NeoUsuario: Number(user?.idUsuario),
      },
    };
  }

  private buildBodyNatural(
    emp: PerfilEmpresarioCache,
    solicitud: {
      idSolicitud?: string | null;
      monto: number;
      plazo: number;
      destino: string;
      tipoLinea?: string | null;
      idsDestinoCredito?: number[];
      redesSociales?: string[] | null;
      redesSocialesDetalle?: PerfilRedSocialDetalle[] | null;
      camposLlenados?: number | null;
      camposObligatorios?: number | null;
      porcentajeSobreTi?: number | null;
      porcentajeSobreNegocio?: number | null;
      porcentajeSobreIngresos?: number | null;
      autorizacionCentrales?: boolean | null;
    }
  ): Record<string, unknown> {
    const user = this.user.getUser();
    const toDate = (v: unknown): string | null => {
      if (v == null || v === '') {
        return null;
      }
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) {
        return v.slice(0, 10);
      }
      try {
        return new Date(v as string).toISOString().slice(0, 10);
      } catch {
        return null;
      }
    };
    const numOrNull = (v: unknown): number | null => {
      if (v == null || v === '') {
        return null;
      }
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const conteo = this.progress.getCamposConteo();
    const camposLlenados = solicitud.camposLlenados ?? conteo.camposLlenados;
    const camposObligatorios =
      solicitud.camposObligatorios ?? conteo.camposObligatorios;

    return {
      idEmpresario: Number(emp.idEmpresario ?? user?.idEmpresario),
      idEmpresarioNatural: Number(
        emp.idEmpresarioNatural ?? user?.idEmpresarioNatural
      ),
      razonSocial: emp.razonSocial ?? '',
      NombreCompleto: emp.razonSocial ?? '',
      correoElectronico: emp.correoElectronico ?? '',
      fechaNacimiento: toDate(emp.fechaNacimiento),
      fechaExpedicionDocumento: toDate(emp.fechaExpedicionDocumento),
      idDepartamento: numOrNull(emp.idDepartamento),
      idCiudad: numOrNull(emp.idCiudad),
      idLocalidad: numOrNull(emp.idLocalidad),
      barrioResidencia: emp.barrioResidencia ?? '',
      direccionResidencia: emp.direccionResidencia ?? '',
      idEstadoCivil: numOrNull(emp.idEstadoCivil),
      idNivelEstudios: numOrNull(emp.idNivelEstudios),
      idTipoVivienda: numOrNull(emp.idTipoVivienda),
      personasACargo:
        emp.personasACargo == null || emp.personasACargo === ''
          ? ''
          : Number(emp.personasACargo),
      seguridadSocial: emp.seguridadSocial,
      sisben: emp.sisben ?? false,
      idSisben: emp.sisben ? numOrNull(emp.idSisben) : null,
      declaraRenta:
        emp.declaraRenta === true || emp.declaraRenta === false
          ? emp.declaraRenta
          : null,
      creditoAnterior: numOrNull(emp.creditoAnterior),
      fechaCreditoAnterior:
        Number(emp.creditoAnterior) === 1
          ? toDate(emp.fechaCreditoAnterior)
          : '',
      fechaRegistroActividadEconomica: toDate(
        emp.fechaRegistroActividadEconomica
      ),
      anioActivos: emp.anioActivos ?? '',
      nivelActivos: numOrNull(emp.nivelActivos) ?? 0,
      idPaisOrigen: numOrNull(emp.idPaisOrigen),
      redesSocialesDetalle: solicitud.redesSocialesDetalle ?? null,
      /** Solo URLs sueltas si no hay detalle; el back prioriza redesSocialesDetalle. */
      redesSociales:
        solicitud.redesSocialesDetalle != null
          ? null
          : solicitud.redesSociales ?? [],
      idsDestinoCredito: solicitud.idsDestinoCredito ?? [],
      camposLlenados,
      camposObligatorios,
      porcentajeSobreTi: solicitud.porcentajeSobreTi ?? null,
      porcentajeSobreNegocio: solicitud.porcentajeSobreNegocio ?? null,
      porcentajeSobreIngresos: solicitud.porcentajeSobreIngresos ?? null,
      autorizacionCentrales: solicitud.autorizacionCentrales ?? false,
      flujoCajaMesCorteInformacion: emp.flujoCajaMesCorteInformacion ?? '',
      flujoCajaVentas: numOrNull(emp.flujoCajaVentas) ?? 0,
      flujoCajaOtrosIngresos: numOrNull(emp.flujoCajaOtrosIngresos) ?? 0,
      flujoCajaFuenteOtrosIngresos: emp.flujoCajaFuenteOtrosIngresos ?? '',
      flujoCajaPagoProveedores: numOrNull(emp.flujoCajaPagoProveedores) ?? 0,
      flujoCajaArriendo: numOrNull(emp.flujoCajaArriendo) ?? 0,
      flujoCajaAdministracion: numOrNull(emp.flujoCajaAdministracion) ?? 0,
      flujoCajaServiciosPublicos: numOrNull(emp.flujoCajaServiciosPublicos) ?? 0,
      flujoCajaSueldoPropio: numOrNull(emp.flujoCajaSueldoPropio) ?? 0,
      flujoCajaSueldoEmpleados: numOrNull(emp.flujoCajaSueldoEmpleados) ?? 0,
      flujoCajaPlanCelular: numOrNull(emp.flujoCajaPlanCelular) ?? 0,
      flujoCajaTarjetaCredito: numOrNull(emp.flujoCajaTarjetaCredito) ?? 0,
      flujoCajaCuotaCreditos: numOrNull(emp.flujoCajaCuotaCreditos) ?? 0,
      flujoCajaSaldoDeudas: numOrNull(emp.flujoCajaSaldoDeudas) ?? 0,
      flujoCajaOtrosGastos: numOrNull(emp.flujoCajaOtrosGastos) ?? 0,
      flujoCajaFuenteOtrosGastos: emp.flujoCajaFuenteOtrosGastos ?? '',
      solicitud: {
        idSolicitud: solicitud.idSolicitud || null,
        monto: solicitud.monto,
        plazo: solicitud.plazo,
        destino: solicitud.destino,
        tipoLinea: solicitud.tipoLinea || null,
        idUsuario_NeoUsuario: Number(user?.idUsuario),
      },
    };
  }

  /**
   * Clave de unicidad: stem sin extensión (como files.form), case-insensitive.
   * `mi.foto.final.jpg` → `mi.foto.final`
   */
  private claveNombreArchivo(fileName: string): string {
    const base = String(fileName || '').trim();
    if (!base) {
      return '';
    }
    const idx = base.lastIndexOf('.');
    const stem = idx > 0 ? base.slice(0, idx) : base;
    return stem.toLowerCase();
  }

  private registrarNombreTrasSubida(
    claveNueva: string,
    claveExcluida: string
  ): void {
    if (claveExcluida && claveExcluida !== claveNueva) {
      this.nombresArchivosCargados.delete(claveExcluida);
    }
    if (claveNueva) {
      this.nombresArchivosCargados.add(claveNueva);
    }
  }

  private liberarNombreArchivo(fileName: string | null | undefined): void {
    const clave = this.claveNombreArchivo(fileName ?? '');
    if (clave) {
      this.nombresArchivosCargados.delete(clave);
    }
  }

  private sincronizarNombresDesdeSolicitud(
    archivos: UploadedTipoDocumento[],
    fotos: UploadedTipoDocumento[]
  ): void {
    for (const tipo of [...archivos, ...fotos]) {
      for (const doc of tipo.neoDocumentosSolicitudDTO ?? []) {
        const clave = this.claveNombreArchivo(doc.nombreDocumento ?? '');
        if (clave) {
          this.nombresArchivosCargados.add(clave);
        }
      }
    }
  }
}

/** Ids N:M de destinos de crédito (lista numérica). */
function normalizeIdsDestinoCredito(raw: unknown): number[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
}

/** Normaliza RedesSociales de cargarDatos (strings u objetos DTO). */
function normalizeRedesDetalle(raw: unknown): PerfilRedSocialDetalle[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: PerfilRedSocialDetalle[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      const url = item.trim();
      if (url) {
        out.push({ url });
      }
      continue;
    }
    if (item && typeof item === 'object') {
      const row = item as {
        url?: string;
        idCatalogoRedSocial?: number | null;
      };
      const url = String(row.url ?? '').trim();
      if (!url) {
        continue;
      }
      out.push({
        url,
        idCatalogoRedSocial:
          row.idCatalogoRedSocial != null
            ? Number(row.idCatalogoRedSocial)
            : null,
      });
    }
  }
  return out;
}
